'use client';

import React from 'react';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { cn } from 'lib/utils';
import { trackEvent, type SegmentEventProperties } from 'lib/analytics';

// Tracking Types ---

type TrackingProps = {
  trackingProps?: {
    event: string;
    properties: SegmentEventProperties;
  };
};

// Original Component Types ---

type CommonProps = {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'primary-white' | 'neutral' | 'neutral-dark';
};

type AnchorOnlyProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick' | 'className'
>;

type ButtonOnlyProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'type' | 'className'
> & {
  type?: 'button' | 'submit' | 'reset';
};

type LinkVariantProps = CommonProps &
  AnchorOnlyProps &
  Omit<NextLinkProps, 'href'> & {
    href: NextLinkProps['href'];
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  } & TrackingProps;

type ButtonVariantProps = CommonProps &
  ButtonOnlyProps & {
    onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
    href?: never;
    disabled?: boolean;
  } & TrackingProps;

export type LinkProps = LinkVariantProps | ButtonVariantProps;

const TrackedLink = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  LinkProps
>(
  (
    {
      children,
      className: classNameProp,
      variant = 'primary',
      trackingProps,
      ...rest
    },
    ref
  ) => {
    const className = cn(
      'font-normal underline rounded focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500 transition-all [&>svg]:transition-colors cursor-pointer disabled:cursor-not-allowed',
      {
        'text-clay-50 hover:text-athletic-200 hover:text-stroke-athletic-200 [&>svg]:fill-clay-50 hover:[&>svg]:fill-athletic-200':
          variant === 'primary',
        'text-clay-50 hover:text-white hover:text-stroke-white [&>svg]:fill-clay-50 hover:[&>svg]:fill-white':
          variant === 'primary-white',
        'text-neutral-200 hover:text-athletic-200 hover:text-stroke-athletic-200 [&>svg]:fill-neutral-200 hover:[&>svg]:fill-athletic-200':
          variant === 'neutral',
        'text-neutral-300 hover:text-athletic-200 hover:text-stroke-athletic-200 [&>svg]:fill-neutral-300 hover:[&>svg]:fill-athletic-200':
          variant === 'neutral-dark',
      },
      classNameProp
    );

    if (!('href' in rest)) {
      const {
        onClick: originalOnClick,
        type = 'button',
        ...buttonProps
      } = rest as ButtonVariantProps;

      const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (trackingProps && trackingProps.event) {
          trackEvent(trackingProps.event, trackingProps.properties);
        }
        if (originalOnClick) {
          originalOnClick(e);
        }
      };

      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          {...{ className, type }}
          onClick={handleButtonClick}
          {...buttonProps}
        >
          {children}
        </button>
      );
    }

    const linkProps = rest as LinkVariantProps;
    const { onClick: originalOnClick } = linkProps;

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (trackingProps && trackingProps.event) {
        trackEvent(trackingProps.event, trackingProps.properties);
      }
      if (originalOnClick) {
        originalOnClick(e);
      }
    };

    return (
      <NextLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...{ className }}
        {...linkProps}
        onClick={handleLinkClick}
      >
        {children}
      </NextLink>
    );
  }
);

TrackedLink.displayName = 'TrackedLink';

export default TrackedLink;
